import React, { Dispatch, SetStateAction, createContext, useMemo } from 'react';
import { TFunction } from 'i18next';
import { useWasteChecksheetDetail } from '../hooks/useWasteChecksheetDetail';
import { ExtendedGetItemsResponse } from '../interfaces/ExtendedGetItemsResponse';
import { ItemWasteChecksheetData, SaveStatus, WasteLogReason } from '../interfaces/ItemWasteChecksheetData';
import { ItemPreview } from '../interfaces/ItemsPreview';
import { Blocker } from 'react-router-dom';
import { Category } from '../interfaces/Category';
import { WasteItemUomInfo } from '../../../app/services/api/generated';

export interface WasteChecksheetDetailValues {
  t: TFunction<'createStockCount', undefined>;
  isLoading: boolean;
  isDirty: boolean;
  showSubmitAlert: boolean;
  itemsData: ExtendedGetItemsResponse;
  itemsByCategory: Category[];
  isArchived: boolean;
  submittedBy: string | null;
  submittedDate: string | undefined;
  loadData: () => void;
  updateCategoryItems: (itemId: number | undefined, fromStatus: SaveStatus | undefined, toStatus: SaveStatus) => void;
  handleLeavePageModalCancel: () => void;
  handleLeavePageModalClose: () => void;
  handleLeavePageModalConfirm: () => void;
  handleItemFocus: (itemId: number) => void;
  handleSaveConfirmModal: () => void;
  handleSubmitButton: () => void;
  handleSubmitCancelModal: () => void;
  handleReset: () => void;
  isSaving: boolean;
  isSubmitting: boolean;
  showSavedAlert: boolean;
  showErrorSavedAlert: boolean;
  showErrorSubmitAlert: boolean;
  openSubmitModal: boolean;
  setShowSavedAlert: (value: boolean) => void;
  setShowErrorSavedAlert: (value: boolean) => void;
  setShowErrorSubmitAlert: (value: boolean) => void;
  printTableContainer: () => void;
  hasPermissionToWrite: boolean;
  isMobile: boolean;
  isSaveSubmitEnabled: () => boolean;
  isEditMode: boolean;
  handleQuantityChange: (categoryIndex: number, itemIndex: number, field: keyof ItemWasteChecksheetData, value: string) => void;
  handleUomChange: (categoryIndex: number, itemIndex: number, field: keyof ItemWasteChecksheetData, uom: string) => void;
  handleConfirmSubmitModal: () => void;
  itemsPreviewOnSubmitModal: ItemPreview[];
  modifiedItems: Set<number>;
  blocker: Blocker;
  setShowSubmitAlert: Dispatch<SetStateAction<boolean>>;
  wasteLogReasons: WasteLogReason[];
  isWasteLogUnitSupportEnabled: boolean;
  itemConversions: Record<string, WasteItemUomInfo[]>;
  currencySymbol: string;
  searchText: string;
  setSearchText: (value: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (value: string[]) => void;
  allCategoryNames: string[];
  filteredItemsByCategory: { category: Category; originalCategoryIndex: number }[];
}

export const WasteChecksheetDetailContext = createContext<null | WasteChecksheetDetailValues>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const WasteChecksheetDetailProvider = ({ children }: ProviderProps) => {

  const {
    t,
    isLoading,
    isDirty,
    showSubmitAlert,
    itemsData,
    itemsByCategory,
    isArchived,
    submittedBy,
    submittedDate,
    loadData,
    updateCategoryItems,
    handleLeavePageModalCancel,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleItemFocus,
    handleSaveConfirmModal,
    handleSubmitButton,
    handleSubmitCancelModal,
    handleReset,
    isSaving,
    isSubmitting,
    showSavedAlert,
    showErrorSavedAlert,
    showErrorSubmitAlert,
    openSubmitModal,
    setShowSavedAlert,
    setShowErrorSavedAlert,
    setShowErrorSubmitAlert,
    printTableContainer,
    hasPermissionToWrite,
    isMobile,
    isSaveSubmitEnabled,
    isEditMode,
    handleQuantityChange,
    handleUomChange,
    handleConfirmSubmitModal,
    itemsPreviewOnSubmitModal,
    modifiedItems,
    blocker,
    setShowSubmitAlert,
    wasteLogReasons,
    isWasteLogUnitSupportEnabled,
    itemConversions,
    currencySymbol,
    searchText,
    setSearchText,
    selectedCategories,
    setSelectedCategories,
    allCategoryNames,
    filteredItemsByCategory,
    transactionValidationDialog,
  } = useWasteChecksheetDetail();

  // Passed values must match the types defined in the interface.
  const passedValue: WasteChecksheetDetailValues = {
    t,
    isLoading,
    isDirty,
    showSubmitAlert,
    itemsData,
    itemsByCategory,
    isArchived,
    submittedBy,
    submittedDate,
    loadData,
    updateCategoryItems,
    handleLeavePageModalCancel,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    handleItemFocus,
    handleSaveConfirmModal,
    handleSubmitButton,
    handleSubmitCancelModal,
    handleReset,
    isSaving,
    isSubmitting,
    showSavedAlert,
    showErrorSavedAlert,
    showErrorSubmitAlert,
    openSubmitModal,
    setShowSavedAlert,
    setShowErrorSavedAlert,
    setShowErrorSubmitAlert,
    printTableContainer,
    hasPermissionToWrite,
    isMobile,
    isSaveSubmitEnabled,
    isEditMode,
    handleQuantityChange,
    handleUomChange,
    handleConfirmSubmitModal,
    itemsPreviewOnSubmitModal,
    modifiedItems,
    blocker,
    setShowSubmitAlert,
    wasteLogReasons,
    isWasteLogUnitSupportEnabled,
    itemConversions,
    currencySymbol,
    searchText,
    setSearchText,
    selectedCategories,
    setSelectedCategories,
    allCategoryNames,
    filteredItemsByCategory,
  };

  const fireBaseProviderValue = useMemo(() => ({ passedValue }), [passedValue]);

  return (
    <WasteChecksheetDetailContext.Provider value={fireBaseProviderValue.passedValue}>
      {children}
      {transactionValidationDialog}
    </WasteChecksheetDetailContext.Provider>
  );
};